import type { UnitListFilters } from "~/types/unit";
import { LISTING_FEATURE_KEYS } from "~/types/listing";
import {
  encodeLocationsForApi,
  getFilterLocations,
  normalizeLocationTerms,
  parseLocationsFromSearchParams,
  withFilterLocations,
} from "./listingLocations";

const FILTER_PARAM_KEYS = [
  "propertyId",
  "location",
  "numBedroom",
  "numBathroom",
  ...LISTING_FEATURE_KEYS,
] as const;

function parseBooleanParam(value: string | null): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

/** Legacy invite links used `propertyID`; normalize to `propertyId`. */
export function parseListingFiltersFromSearchParams(
  searchParams: URLSearchParams
): UnitListFilters {
  const propertyId =
    searchParams.get("propertyId")?.trim() ||
    searchParams.get("propertyID")?.trim() ||
    undefined;

  const locations = parseLocationsFromSearchParams(searchParams);
  const numBedroom = searchParams.get("numBedroom")?.trim() || undefined;
  const numBathroom = searchParams.get("numBathroom")?.trim() || undefined;

  const filters: UnitListFilters = {
    ...(propertyId ? { propertyId } : {}),
    ...(locations.length ? { locations } : {}),
    ...(numBedroom ? { numBedroom } : {}),
    ...(numBathroom ? { numBathroom } : {}),
  };

  for (const key of LISTING_FEATURE_KEYS) {
    const value = parseBooleanParam(searchParams.get(key));
    if (value === true) {
      filters[key] = true;
    }
  }

  return filters;
}

export function countActiveListingFilters(
  filters: UnitListFilters,
  options?: { excludePropertyId?: boolean; excludeLocation?: boolean }
): number {
  let count = 0;
  if (filters.propertyId && !options?.excludePropertyId) count += 1;
  if (getFilterLocations(filters).length > 0 && !options?.excludeLocation) {
    count += 1;
  }
  if (filters.numBedroom) count += 1;
  if (filters.numBathroom) count += 1;
  for (const key of LISTING_FEATURE_KEYS) {
    if (filters[key] === true) count += 1;
  }
  return count;
}

export function mergeListingFiltersIntoSearchParams(
  current: URLSearchParams,
  filters: UnitListFilters
): URLSearchParams {
  const next = new URLSearchParams(current.toString());
  next.delete("propertyID");
  for (const key of FILTER_PARAM_KEYS) {
    next.delete(key);
  }

  if (filters.propertyId) {
    next.set("propertyId", filters.propertyId);
  }

  for (const location of getFilterLocations(filters)) {
    next.append("location", location);
  }

  if (filters.numBedroom) {
    next.set("numBedroom", filters.numBedroom);
  }
  if (filters.numBathroom) {
    next.set("numBathroom", filters.numBathroom);
  }
  for (const key of LISTING_FEATURE_KEYS) {
    if (filters[key] === true) {
      next.set(key, "true");
    }
  }
  return next;
}

/** Build query string for navigating to the listing browse page. */
export function listingFiltersToSearchParams(
  filters: UnitListFilters
): URLSearchParams {
  return mergeListingFiltersIntoSearchParams(new URLSearchParams(), filters);
}

export {
  encodeLocationsForApi,
  getFilterLocations,
  normalizeLocationTerms,
  withFilterLocations,
};
